import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, CheckCircle2, Info, Loader2, PlugZap, Save, Network } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { formatarDataHora } from '@/lib/utils';
import type { ConexaoSisreg } from '@/features/historico-paciente/tipos';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { Botao } from '@/components/ui/button';
import { Entrada } from '@/components/ui/input';
import { Selecao } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Etiqueta } from '@/components/ui/badge';
import { Esqueleto } from '@/components/ui/skeleton';
import {
  Cartao,
  CartaoCabecalho,
  CartaoConteudo,
  CartaoDescricao,
  CartaoTitulo,
} from '@/components/ui/card';
import { cn, paraBooleano } from '@/lib/utils';

const esquema = z.object({
  baseUrl: z.string().min(1, 'Informe o endereço da API.'),
  indiceMarcacao: z.string().min(1, 'Informe o índice de marcações.'),
  indiceSolicitacao: z.string().min(1, 'Informe o índice de solicitações.'),
  usuario: z.string().min(1, 'Informe o usuário da API.'),
  senha: z.string().optional(),
  timeoutMs: z.coerce.number().int().min(5000).max(120000),
  ativo: z.boolean(),
});

type Formulario = z.infer<typeof esquema>;

interface ResultadoTeste {
  ok: boolean;
  mensagem: string;
}

export function PaginaSisreg() {
  const queryClient = useQueryClient();
  const [resultado, setResultado] = React.useState<ResultadoTeste | null>(null);

  const { data: conexao, isPending } = useQuery({
    queryKey: ['sisreg-conexao'],
    queryFn: () => api.get<ConexaoSisreg>('/sisreg/conexao'),
  });

  const formulario = useForm<Formulario>({
    resolver: zodResolver(esquema),
    values: {
      baseUrl: conexao?.baseUrl ?? 'https://sisreg-es.saude.gov.br',
      indiceMarcacao: conexao?.indiceMarcacao ?? '',
      indiceSolicitacao: conexao?.indiceSolicitacao ?? '',
      usuario: conexao?.usuario ?? '',
      senha: '',
      timeoutMs: conexao?.timeoutMs ?? 30_000,
      ativo: conexao?.ativo ?? false,
    },
  });

  const testar = useMutation({
    mutationFn: (dados: Formulario) =>
      api.post<ResultadoTeste>('/sisreg/conexao/testar', {
        baseUrl: dados.baseUrl,
        indiceMarcacao: dados.indiceMarcacao,
        indiceSolicitacao: dados.indiceSolicitacao,
        usuario: dados.usuario,
        senha: dados.senha || undefined,
        timeoutMs: dados.timeoutMs,
      }),
    onSuccess: async (r) => {
      setResultado(r);
      await queryClient.invalidateQueries({ queryKey: ['sisreg-conexao'] });
    },
    onError: (erro) =>
      setResultado({
        ok: false,
        mensagem: erro instanceof ErroApi ? erro.mensagemUsuario : 'Falha ao testar.',
      }),
  });

  const salvar = useMutation({
    mutationFn: (dados: Formulario) =>
      api.patch<ConexaoSisreg>('/sisreg/conexao', { ...dados, senha: dados.senha || undefined }),
    onSuccess: async () => {
      toast.success('Configuração salva.');
      formulario.setValue('senha', '');
      await queryClient.invalidateQueries({ queryKey: ['sisreg-conexao'] });
      await queryClient.invalidateQueries({ queryKey: ['historico-paciente'] });
    },
    onError: (erro) =>
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível salvar.'),
  });

  const aoSalvar = formulario.handleSubmit((d) => salvar.mutate(d));
  const aoTestar = formulario.handleSubmit((d) => testar.mutate(d));

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Esqueleto className="h-8 w-full max-w-80" />
        <Esqueleto className="h-96 w-full max-w-2xl" />
      </div>
    );
  }

  const ativo = formulario.watch('ativo');

  return (
    <div className="max-w-2xl">
      <CabecalhoPagina
        titulo="Integração com o SISREG 3"
        descricao="Consulta somente-leitura à API do SISREG, usada no histórico do paciente."
      />

      <div className="border-status-enviado-borda bg-status-enviado-fundo text-status-enviado-texto mb-4 flex items-start gap-2 rounded-md border p-3 text-sm">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <div>
          <p className="font-medium">Os índices são filtrados pelo município.</p>
          <p className="mt-0.5">
            O nome termina com a UF e o nome do município (ex.:{' '}
            <code>marcacao-ambulatorial-pb-guarabira</code>), não com o código IBGE. Desativada, a
            aba SISREG do histórico apenas avisa que a integração não está configurada.
          </p>
        </div>
      </div>

      <Cartao>
        <CartaoCabecalho>
          <CartaoTitulo className="flex items-center gap-2">
            <Network className="size-4" aria-hidden="true" />
            Dados da conexão
          </CartaoTitulo>
          <CartaoDescricao>
            {conexao?.ultimoTesteEm ? (
              <span className="flex flex-wrap items-center gap-1.5">
                Último teste em {formatarDataHora(conexao.ultimoTesteEm)}
                <Etiqueta tom={conexao.ultimoTesteOk ? 'sucesso' : 'perigo'}>
                  {conexao.ultimoTesteOk ? 'conectado' : 'com problema'}
                </Etiqueta>
              </span>
            ) : (
              'A conexão ainda não foi testada.'
            )}
          </CartaoDescricao>
        </CartaoCabecalho>

        <CartaoConteudo>
          <form onSubmit={(e) => void aoSalvar(e)} noValidate className="flex flex-col gap-4">
            <Campo
              id="sisreg-url"
              rotulo="Endereço da API"
              obrigatorio
              erro={formulario.formState.errors.baseUrl?.message}
            >
              <Entrada
                placeholder="https://sisreg-es.saude.gov.br"
                {...formulario.register('baseUrl')}
              />
            </Campo>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                id="sisreg-indice-marcacao"
                rotulo="Índice de marcações"
                obrigatorio
                erro={formulario.formState.errors.indiceMarcacao?.message}
              >
                <Entrada
                  placeholder="marcacao-ambulatorial-uf-municipio"
                  {...formulario.register('indiceMarcacao')}
                />
              </Campo>

              <Campo
                id="sisreg-indice-solicitacao"
                rotulo="Índice de solicitações"
                obrigatorio
                erro={formulario.formState.errors.indiceSolicitacao?.message}
              >
                <Entrada
                  placeholder="solicitacao-ambulatorial-uf-municipio"
                  {...formulario.register('indiceSolicitacao')}
                />
              </Campo>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                id="sisreg-timeout"
                rotulo="Tempo limite (ms)"
                ajuda="Entre 5.000 e 120.000."
                erro={formulario.formState.errors.timeoutMs?.message}
              >
                <Entrada type="number" inputMode="numeric" {...formulario.register('timeoutMs')} />
              </Campo>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                id="sisreg-usuario"
                rotulo="Usuário"
                obrigatorio
                erro={formulario.formState.errors.usuario?.message}
              >
                <Entrada autoComplete="off" {...formulario.register('usuario')} />
              </Campo>

              <Campo
                id="sisreg-senha"
                rotulo="Senha"
                ajuda={
                  conexao?.temSenha
                    ? 'Já existe uma senha salva. Deixe em branco para mantê-la.'
                    : 'Nenhuma senha salva ainda.'
                }
              >
                <Entrada
                  type="password"
                  autoComplete="new-password"
                  placeholder={conexao?.temSenha ? '••••••••' : ''}
                  {...formulario.register('senha')}
                />
              </Campo>
            </div>

            <Campo
              id="sisreg-ativo"
              rotulo="Situação da integração"
              ajuda="Desativada, o histórico não consulta o SISREG."
            >
              <Selecao
                {...formulario.register('ativo', {
                  setValueAs: paraBooleano,
                })}
              >
                <option value="false">Desativada</option>
                <option value="true">Ativa (API do SISREG)</option>
              </Selecao>
            </Campo>

            {ativo && !conexao?.ultimoTesteOk && (
              <p className="text-alerta flex items-start gap-1.5 text-xs" role="status">
                <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                Teste a conexão antes de ativar: sem isso, a aba SISREG do histórico mostra erro a
                cada consulta.
              </p>
            )}

            {resultado && (
              <div
                role="status"
                className={cn(
                  'flex items-start gap-2 rounded-md border p-3 text-sm',
                  resultado.ok
                    ? 'border-status-agendado-borda bg-status-agendado-fundo text-status-agendado-texto'
                    : 'border-status-devolvido-borda bg-status-devolvido-fundo text-status-devolvido-texto',
                )}
              >
                {resultado.ok ? (
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                ) : (
                  <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                )}
                <span className="min-w-0 break-words">{resultado.mensagem}</span>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Botao
                type="button"
                variante="contorno"
                onClick={() => void aoTestar()}
                disabled={testar.isPending}
              >
                {testar.isPending ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : (
                  <PlugZap aria-hidden="true" />
                )}
                {testar.isPending ? 'Testando…' : 'Testar conexão'}
              </Botao>

              <Botao type="submit" disabled={salvar.isPending}>
                {salvar.isPending ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : (
                  <Save aria-hidden="true" />
                )}
                Salvar
              </Botao>
            </div>
          </form>
        </CartaoConteudo>
      </Cartao>
    </div>
  );
}
